// src/services/CalendarSyncService.js - ENHANCED with Smart Duplicate Prevention
import { FirebaseService } from './FirebaseService';
import { GoogleCalendarAPIService } from './GoogleCalendarAPIService';

export class CalendarSyncService {
  static getGoogleCalendarId(business) {
    const calendarId = business?.google_calendar_id
      || business?.calendar_integration?.google_calendar_id
      || business?.calendarIntegration?.googleCalendarId
      || business?.calendarIntegration?.google_calendar_id
      || business?.calendarId
      || null;

    if (!calendarId) {
      throw new Error('No Google Calendar ID found for business');
    }

    return calendarId;
  }
  
  // ==========================================
  // ENHANCED SINGLE BOOKING SYNC WITH DUPLICATE PREVENTION
  // ==========================================

  static async syncBookingToCalendar(businessId, booking) {
    // Skip bookings already synced by the Python backend
    // (identified by having a googleEventId that matches the Firestore doc ID)
    if (booking.googleEventId && booking.googleEventId === booking.id) {
      return { success: true, status: 'already_synced', skipped: true };
    }

    if (booking.status === 'cancelled') {
      return { success: true, status: 'skipped_cancelled', skipped: true };
    }

    const bookingId = booking.id || booking.bookingId;
    
    console.log(`🔍 ENHANCED SYNC: Starting calendar sync for booking ${bookingId}`);
    console.log(`   📋 Current sync status: ${booking.calendar_sync_status || 'unknown'}`);
    console.log(`   🆔 Current event ID: ${booking.calendar_event_id || 'none'}`);
    
    // ✅ SMART DUPLICATE PREVENTION
    if (booking.calendar_event_id && booking.calendar_sync_status === 'synced') {
      console.log(`🚫 DUPLICATE PREVENTED: Booking ${bookingId} already synced (Event: ${booking.calendar_event_id})`);
      return {
        success: true,
        eventId: booking.calendar_event_id,
        message: 'Booking already synced to calendar',
        prevented_duplicate: true,
        htmlLink: booking.calendar_html_link
      };
    }

    // ✅ SMART AVAILABILITY CHECK
    const isAvailable = await this.checkCalendarAvailability(businessId);
    if (!isAvailable) {
      console.log(`📋 No calendar integration for business ${businessId}`);
      return { 
        success: false, 
        reason: 'no_calendar_integration',
        message: 'Calendar integration not available'
      };
    }
    
    try {
      console.log(`📅 Executing calendar sync via Python backend for booking ${bookingId}...`);
      
      // Get calendar integration details
      const integration = await FirebaseService.checkCalendarIntegration(businessId);
      const calendarId = this.getGoogleCalendarId(integration);
      
      // Call secure Python backend
      const result = await GoogleCalendarAPIService.createCalendarEvent(calendarId, booking);
      
      if (result.success) {
        console.log(`✅ ENHANCED SYNC SUCCESS: Calendar event created for booking ${bookingId}`);
        console.log(`   🆔 Event ID: ${result.eventId}`);
        console.log(`   🔗 HTML Link: ${result.htmlLink || 'none'}`);
        
        // ✅ UPDATE FIREBASE WITH SYNC STATUS (prevents future duplicates)
        try {
          await FirebaseService.updateBooking(
            businessId,
            booking.customerId,
            bookingId,
            {
              calendar_event_id: result.eventId,
              calendar_sync_status: 'synced',
              calendar_html_link: result.htmlLink,
              calendar_synced_at: new Date().toISOString()
            }
          );
          console.log(`📝 Updated booking ${bookingId} with calendar sync status`);
        } catch (updateError) {
          console.warn(`⚠️ Failed to update booking sync status:`, updateError);
          // Don't fail the sync if status update fails
        }
        
        return {
          success: true,
          eventId: result.eventId,
          htmlLink: result.htmlLink,
          message: 'Booking synced to Google Calendar successfully',
          calendarUrl: `https://calendar.google.com/calendar/embed?src=${calendarId}`,
          sync_timestamp: new Date().toISOString()
        };
      } else {
        throw new Error(result.error || 'Calendar sync failed');
      }
      
    } catch (error) {
      console.error(`❌ ENHANCED SYNC FAILED for booking ${bookingId}:`, error);
      
      // ✅ UPDATE FIREBASE WITH FAILED STATUS
      try {
        await FirebaseService.updateBooking(
          businessId,
          booking.customerId,
          bookingId,
          {
            calendar_sync_status: 'failed',
            calendar_sync_error: error.message,
            calendar_sync_failed_at: new Date().toISOString()
          }
        );
      } catch (updateError) {
        console.warn(`⚠️ Failed to update booking error status:`, updateError);
      }
      
      return {
        success: false,
        error: error.message,
        message: `Failed to sync booking: ${error.message}`
      };
    }
  }

  // ==========================================
  // ENHANCED BULK SYNC WITH SMART FILTERING
  // ==========================================

  static async syncAllBookingsToCalendar(businessId, options = {}) {
    const isAvailable = await this.checkCalendarAvailability(businessId);
    
    if (!isAvailable) {
      console.log('📋 Calendar not integrated, skipping bulk sync');
      return { success: false, reason: 'no_calendar_integration' };
    }
    
    try {
      console.log('🔄 ENHANCED BULK SYNC: Starting intelligent bulk calendar sync...');
      console.log('📋 Options:', options);
      
      // Get all business bookings from Firebase
      const allBookings = await FirebaseService.getAllBusinessBookings(businessId);
      console.log(`📊 Found ${allBookings.length} total bookings for business`);
      
      if (allBookings.length === 0) {
        return {
          success: true,
          message: 'No bookings found to sync',
          summary: { total: 0, processed: 0, successful: 0, failed: 0, skipped: 0 }
        };
      }
      
      // ✅ SMART FILTERING: Only sync bookings that need it
      const filteredBookings = allBookings.filter(booking => {
        // Skip if already synced (unless force option is true)
        if (!options.force && booking.calendar_event_id && booking.calendar_sync_status === 'synced') {
          return false;
        }
        
        // Skip non-confirmed bookings
        if (booking.status !== 'confirmed') {
          return false;
        }
        
        // Skip very recent bookings if skipRecent is true (default behavior)
        if (options.skipRecent !== false) {
          const bookingAge = Date.now() - new Date(booking.created_at || 0).getTime();
          if (bookingAge < 10000) { // Less than 10 seconds old
            console.log(`⏰ Skipping very recent booking ${booking.id} in bulk sync`);
            return false;
          }
        }
        
        return true;
      });
      
      console.log(`🎯 SMART FILTERING: ${filteredBookings.length}/${allBookings.length} bookings need sync`);
      console.log(`   📋 Reason: ${options.reason || 'manual_bulk_sync'}`);
      
      if (filteredBookings.length === 0) {
        return {
          success: true,
          message: 'All bookings already synced or filtered out',
          summary: { 
            total: allBookings.length, 
            processed: 0, 
            successful: 0, 
            failed: 0, 
            skipped: allBookings.length 
          }
        };
      }
      
      // Process bookings in batches to prevent rate limiting
      const batchSize = 5;
      const batches = [];
      for (let i = 0; i < filteredBookings.length; i += batchSize) {
        batches.push(filteredBookings.slice(i, i + batchSize));
      }
      
      console.log(`📦 Processing ${batches.length} batches of up to ${batchSize} bookings each`);
      
      const syncResults = [];
      let successCount = 0;
      let errorCount = 0;
      let skipCount = 0;
      
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        console.log(`📦 Processing batch ${batchIndex + 1}/${batches.length} (${batch.length} bookings)`);
        
        // Process batch with Promise.allSettled for better error handling
        const batchPromises = batch.map(async (booking) => {
          try {
            const result = await this.syncBookingToCalendar(businessId, booking);
            
            if (result.success) {
              if (result.prevented_duplicate) {
                skipCount++;
                return { bookingId: booking.id, status: 'skipped', reason: 'already_synced' };
              } else {
                successCount++;
                return { bookingId: booking.id, status: 'success', eventId: result.eventId };
              }
            } else {
              errorCount++;
              return { bookingId: booking.id, status: 'failed', error: result.error };
            }
            
          } catch (error) {
            errorCount++;
            console.error(`❌ Batch sync error for booking ${booking.id}:`, error);
            return { bookingId: booking.id, status: 'failed', error: error.message };
          }
        });
        
        const batchResults = await Promise.allSettled(batchPromises);
        syncResults.push(...batchResults.map(r => r.value || r.reason));
        
        // Small delay between batches to prevent rate limiting
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      const summary = {
        total: allBookings.length,
        filtered: filteredBookings.length,
        processed: successCount + errorCount + skipCount,
        successful: successCount,
        failed: errorCount,
        skipped: allBookings.length - filteredBookings.length + skipCount
      };
      
      console.log('✅ ENHANCED BULK SYNC COMPLETED:');
      console.log('   📊 Summary:', summary);
      console.log(`   ✅ Successful: ${successCount}`);
      console.log(`   ❌ Failed: ${errorCount}`);
      console.log(`   ⏭️ Skipped: ${summary.skipped}`);
      
      return {
        success: true,
        results: syncResults,
        summary: summary,
        message: `Bulk sync completed: ${successCount} successful, ${errorCount} failed, ${summary.skipped} skipped`
      };
      
    } catch (error) {
      console.error('❌ ENHANCED BULK SYNC FAILED:', error);
      return {
        success: false,
        error: error.message,
        reason: 'bulk_sync_failed'
      };
    }
  }

  // ==========================================
  // CALENDAR AVAILABILITY CHECK (PRESERVED)
  // ==========================================
  
  static async checkCalendarAvailability(businessId) {
    try {
      const integration = await FirebaseService.checkCalendarIntegration(businessId);
      return integration.hasCalendar;
    } catch (error) {
      console.warn('Calendar availability check failed:', error);
      return false;
    }
  }

  // ==========================================
  // UPDATE AND DELETE METHODS (ENHANCED)
  // ==========================================

  static async updateCalendarEvent(businessId, booking) {
    try {
      console.log(`🔄 ENHANCED UPDATE: Updating calendar event for booking ${booking.id}`);
      
      const integration = await FirebaseService.checkCalendarIntegration(businessId);
      const calendarId = this.getGoogleCalendarId(integration);
      
      if (!calendarId || !booking.calendar_event_id) {
        throw new Error('No calendar integration or event ID found');
      }
      
      const result = await GoogleCalendarAPIService.updateCalendarEvent(
        calendarId, 
        booking.calendar_event_id, 
        booking
      );
      
      if (result.success) {
        console.log(`✅ ENHANCED UPDATE SUCCESS: Calendar event updated for booking ${booking.id}`);
        
        // Update Firebase with new sync timestamp
        try {
          await FirebaseService.updateBooking(
            businessId,
            booking.customerId,
            booking.id,
            {
              calendar_sync_status: 'synced',
              calendar_synced_at: new Date().toISOString()
            }
          );
        } catch (updateError) {
          console.warn('Failed to update sync timestamp:', updateError);
        }
        
        return {
          success: true,
          eventId: result.eventId,
          message: 'Calendar event updated successfully'
        };
      } else {
        throw new Error(result.error);
      }
      
    } catch (error) {
      console.error(`❌ ENHANCED UPDATE FAILED for booking ${booking.id}:`, error);
      
      // Mark as failed in Firebase
      try {
        await FirebaseService.updateBooking(
          businessId,
          booking.customerId,
          booking.id,
          {
            calendar_sync_status: 'update_failed',
            calendar_sync_error: error.message
          }
        );
      } catch (updateError) {
        console.warn('Failed to update error status:', updateError);
      }
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  static async deleteCalendarEvent(businessId, booking) {
    try {
      console.log(`🗑️ ENHANCED DELETE: Deleting calendar event for booking ${booking.id}`);
      
      const integration = await FirebaseService.checkCalendarIntegration(businessId);
      const calendarId = this.getGoogleCalendarId(integration);
      
      if (!calendarId) {
        console.warn('No calendar integration found for deletion');
        return { success: true, message: 'No calendar to delete from' };
      }
      
      if (!booking.calendar_event_id) {
        console.log('No calendar event ID found, nothing to delete');
        return { success: true, message: 'No calendar event to delete' };
      }
      
      const result = await GoogleCalendarAPIService.deleteCalendarEvent(
        calendarId,
        booking.calendar_event_id,
        booking.id,
        businessId
      );
      
      if (result.success) {
        console.log(`✅ ENHANCED DELETE SUCCESS: Calendar event deleted for booking ${booking.id}`);
        
        // Clear calendar info from Firebase
        try {
          await FirebaseService.updateBooking(
            businessId,
            booking.customerId,
            booking.id,
            {
              calendar_event_id: null,
              calendar_sync_status: 'deleted',
              calendar_html_link: null,
              calendar_deleted_at: new Date().toISOString()
            }
          );
        } catch (updateError) {
          console.warn('Failed to update deletion status:', updateError);
        }
        
        return {
          success: true,
          message: 'Calendar event deleted successfully'
        };
      } else {
        throw new Error(result.error);
      }
      
    } catch (error) {
      console.error(`❌ ENHANCED DELETE FAILED for booking ${booking.id}:`, error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ==========================================
  // ADDITIONAL UTILITY METHODS (PRESERVED)
  // ==========================================

  static async getCalendarEvents(businessId, startDate, endDate) {
    try {
      console.log('📋 Getting calendar events via Python backend...');
      
      const result = await GoogleCalendarAPIService.getCalendarEvents(businessId, startDate, endDate);
      
      if (result.success) {
        console.log('✅ Retrieved calendar events:', result.events.length);
        return {
          success: true,
          events: result.events
        };
      } else {
        return {
          success: false,
          events: [],
          error: result.error
        };
      }
      
    } catch (error) {
      console.error('❌ Failed to get calendar events:', error);
      return {
        success: false,
        events: [],
        error: error.message
      };
    }
  }

  /**
   * Sync all bookings to calendar (bulk operation via Python backend)
   */
  static async syncAllBookingsToCalendar(businessId) {
    const isAvailable = await this.checkCalendarAvailability(businessId);
    
    if (!isAvailable) {
      console.log('📋 Calendar not integrated, skipping bulk sync');
      return { success: false, reason: 'no_calendar_integration' };
    }
    
    try {
      console.log('🔄 Starting bulk calendar sync via Python backend...');
      
      // Call secure Python backend for bulk sync
      const result = await GoogleCalendarAPIService.syncAllBookingsToCalendar(businessId);
      
      if (result.success) {
        console.log('✅ Bulk calendar sync completed:', result.results);
        return {
          success: true,
          results: result.results,
          message: result.message
        };
      } else {
        throw new Error(result.error);
      }
      
    } catch (error) {
      console.error('❌ Bulk calendar sync failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }


  // ==========================================
  // BULK BOOKING SYNC
  // ==========================================

  /**
   * Sync multiple bookings to calendar (for initial setup or refresh)
   */
  static async syncBookingsToCalendar(businessId, bookings) {
    const isAvailable = await this.checkCalendarAvailability(businessId);
    
    if (!isAvailable) {
      console.log('📋 Calendar not integrated, skipping bulk sync');
      return { 
        success: false, 
        reason: 'no_calendar_integration',
        events: []
      };
    }

    try {
      console.log(`📅 Starting bulk sync for ${bookings.length} bookings...`);
      
      const syncResults = [];
      const events = [];
      let successCount = 0;
      let errorCount = 0;

      for (const booking of bookings) {
        try {
          const result = await this.syncBookingToCalendar(businessId, booking);
          
          syncResults.push({ bookingId: booking.id, result });
          
          if (result.success) {
            successCount++;
            events.push({
              id: result.eventId,
              bookingId: booking.id,
              htmlLink: result.htmlLink
            });
          } else {
            errorCount++;
          }
          
          // Small delay to prevent rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
          
        } catch (error) {
          console.error('❌ Error syncing individual booking:', booking.id, error);
          errorCount++;
          syncResults.push({ 
            bookingId: booking.id, 
            result: { success: false, error: error.message } 
          });
        }
      }

      console.log(`✅ Bulk sync completed: ${successCount}/${successCount + errorCount} bookings synced`);
      
      return {
        success: true,
        events,
        results: syncResults,
        summary: {
          total: bookings.length,
          processed: successCount + errorCount,
          successful: successCount,
          failed: errorCount,
          skipped: bookings.length - (successCount + errorCount)
        }
      };
      
    } catch (error) {
      console.error('❌ Bulk calendar sync failed:', error);
      return {
        success: false,
        error: error.message,
        reason: 'bulk_sync_failed'
      };
    }
  }

  // ==========================================
  // CALENDAR SETUP AND INITIALIZATION
  // ==========================================

  /**
   * Set up Google Calendar integration for a business
   * FIXED: Use existing calendar if available, create only if needed
   */
  static async setupCalendarIntegration(businessId, businessData) {
    try {
      console.log('🔧 Setting up Google Calendar integration...');
      
      // FIXED: Check if calendar already exists
      const existingIntegration = await FirebaseService.checkCalendarIntegration(businessId);
      
      if (existingIntegration.hasCalendar) {
        console.log('✅ Using existing calendar:', existingIntegration.calendarId);
        
        // Authenticate with Google (needed for API access)
        const authResult = await GoogleCalendarAPIService.authenticateUser();
        
        if (!authResult.success) {
          throw new Error(`Authentication failed: ${authResult.error}`);
        }
        
        // Return existing calendar info
        const calendarData = existingIntegration.calendarIntegration || {};
        const calendarId = this.getGoogleCalendarId(existingIntegration);
        return {
          success: true,
          calendarId,
          calendarName: calendarData.calendar_name,
          calendarUrl: calendarData.public_url,
          icsUrl: calendarData.ics_url,
          publicUrl: calendarData.public_url,
          existing: true
        };
      }
      
      // If no existing calendar, create new one
      console.log('📅 No existing calendar found, creating new one...');
      
      // Authenticate with Google
      const authResult = await GoogleCalendarAPIService.authenticateUser();
      
      if (!authResult.success) {
        throw new Error(`Authentication failed: ${authResult.error}`);
      }
      
      // Create business calendar
      const calendarResult = await GoogleCalendarAPIService.createBusinessCalendar(businessData);
      
      if (!calendarResult.success) {
        throw new Error(`Calendar creation failed: ${calendarResult.error}`);
      }
      
      // Store calendar integration info in Firebase
      const integrationData = {
        calendar_integration: {
          google_calendar_id: calendarResult.calendarId,
          calendar_name: calendarResult.calendarName,
          calendar_url: calendarResult.calendarUrl,
          ics_url: calendarResult.icsUrl,
          public_url: calendarResult.publicUrl,
          status: 'active',
          created_at: new Date().toISOString(),
          setup_method: 'google_api'
        }
      };
      
      await FirebaseService.updateBusinessProfile(businessId, integrationData);
      
      console.log('✅ Google Calendar integration setup complete');
      
      return {
        success: true,
        calendarId: calendarResult.calendarId,
        calendarName: calendarResult.calendarName,
        calendarUrl: calendarResult.calendarUrl,
        icsUrl: calendarResult.icsUrl,
        publicUrl: calendarResult.publicUrl,
        created: true
      };
    } catch (error) {
      console.error('❌ Calendar integration setup failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * ADDED: Sync all existing Firestore bookings to Google Calendar
   */
  static async syncAllExistingBookings(businessId) {
    try {
      console.log('🔄 Starting bulk sync of existing bookings...');
      
      // Get all bookings from Firestore
      const allBookings = await FirebaseService.getAllBusinessBookings(businessId);
      console.log(`📋 Found ${allBookings.length} bookings to sync`);
      
      if (allBookings.length === 0) {
        return {
          success: true,
          message: 'No bookings to sync',
          summary: { total: 0, successful: 0, failed: 0 }
        };
      }
      
      // Use the existing bulk sync method
      return await this.syncBookingsToCalendar(businessId, allBookings);
    } catch (error) {
      console.error('❌ Failed to sync existing bookings:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ==========================================
  // EVENT MAPPING UTILITIES
  // ==========================================

  /**
   * Store mapping between booking ID and Google Calendar event ID
   */
  static async storeCalendarEventMapping(businessId, bookingId, googleEventId) {
    try {
      const mappingData = {
        businessId,
        bookingId,
        googleEventId,
        createdAt: new Date().toISOString()
      };
      
      await FirebaseService.setDocument(
        `businesses/${businessId}/calendar_mappings/${bookingId}`,
        mappingData
      );
    } catch (error) {
      console.error('❌ Failed to store calendar event mapping:', error);
    }
  }

  /**
   * Get mapping between booking ID and Google Calendar event ID
   */
  static async getCalendarEventMapping(businessId, bookingId) {
    try {
      return await FirebaseService.getDocument(
        `businesses/${businessId}/calendar_mappings/${bookingId}`
      );
    } catch (error) {
      console.error('❌ Failed to get calendar event mapping:', error);
      return null;
    }
  }

  /**
   * Remove mapping between booking ID and Google Calendar event ID
   */
  static async removeCalendarEventMapping(businessId, bookingId) {
    try {
      await FirebaseService.deleteDocument(
        `businesses/${businessId}/calendar_mappings/${bookingId}`
      );
    } catch (error) {
      console.error('❌ Failed to remove calendar event mapping:', error);
    }
  }

  // ==========================================
  // CALENDAR SUBSCRIPTION URLS
  // ==========================================

  /**
   * Get calendar subscription URLs for universal calendar access
   */
  static async getCalendarSubscriptionURLs(businessId) {
    try {
      // Get calendar integration info from Firebase
      const integration = await FirebaseService.checkCalendarIntegration(businessId);
      
      if (!integration.hasCalendar) {
        return {
          available: false,
          error: 'Calendar integration not set up',
          setupRequired: true
        };
      }

      const calendarData = integration.calendarIntegration || {};
      const calendarId = this.getGoogleCalendarId(integration);
      const calendarName = encodeURIComponent(calendarData.calendar_name || 'Business Calendar');

      return {
        available: true,
        urls: {
          // Google Calendar public link
          googleCalendarURL: calendarData.calendar_url,
          
          // iCal URL (universal calendar subscription)
          iCalURL: calendarData.ics_url,
          
          // CalDAV URL (for advanced calendar apps)
          calDAVURL: `https://calendar.google.com/calendar/dav/${calendarId}/events`,
          
          // Outlook subscription link
          outlookURL: `https://outlook.live.com/calendar/addcalendar?name=${calendarName}&url=${calendarData.ics_url}`,
          
          // Apple Calendar subscription (iOS/macOS)
          appleCalendarURL: calendarData.ics_url.replace('https://', 'webcal://'),
          
          // Public view URL
          publicURL: calendarData.public_url
        },
        calendarName: calendarData.calendar_name,
        instructions: {
          apple: 'Open Calendar app → File → New Calendar Subscription → Paste iCal URL',
          outlook: 'Click Outlook URL or manually add calendar in Outlook settings',
          google: 'Click Google Calendar link to view in your Google Calendar',
          generic: 'Use iCal URL with any calendar application that supports calendar subscriptions'
        }
      };
    } catch (error) {
      console.error('Failed to get calendar subscription URLs:', error);
      return {
        available: false,
        error: error.message,
        setupRequired: true
      };
    }
  }
}
